/**
 * @file   src\Pages\AddSubscription.tsx
 * @brief  AddSubscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useRef, useEffect, Container, Button, Breadcrumb, Row, Col, useOutletContext, useNavigate, useParams, React, useLocation } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWCheckbox from '../components/CWCheckbox';
import CWInputGroup from '../components/CWInputGroup';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { addSubscriptionSchema } from '../validations/addSubscriptionSchema';
import { AMOUNT_MAX_LENGTH, WASH_DETAIL_MAX_LENGTH, WASH_NAME_MAX_LENGTH, WASH_NUMBER_MAX_LENGTH } from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import CWMultiSelect from '../components/CWMultiSelect';
import axios from '../config/axiosConfig';
import { CompanyApi, LocationApi, SubscriptionApi } from '../utils/apiUrls';
import { LocationItem, Option, SubscriptionDataItem } from '../interfaces/subscriptionInterface';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import CWTextarea from '../components/CWTextarea';
import SmallLoader from '../components/SmallLoader';
import { serviceType, userType } from '../utils/enums';
import { CommonMessages } from '../messages/validationMessages';
import { addSelfServeOnlySubscriptionSchema, addSelfServeSubscriptionSchema } from '../validations/addSelfServeSubscriptionSchema';
    interface DiscountData {
  [key: string]: number | string; // allow dynamic keys like discount1...discount10
}
interface ErrorData {
  [key: string]: string;
}
const AddSelfServeSubscription = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false
    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);
    const [companyDetails, setCompanyDetails] = useState<any>([]);
    // State to handle subscription types given in dropdown
    const subscriptionTypes = [
        { label: "Single wash", value: "day" },
        { label: "Monthly", value: "month" },
    ];

      const [isFamilyPlan,setIsFamilyPlan]=useState(false);
        const [familyPlanErrors,setFamilyPlanErrors] =  useState<ErrorData>({});
        const [discountData, setDiscountData] = useState<DiscountData>({
            discount1: "",
            discount2: "",
            discount3: "",
            discount4: "",
            discount5: "",
            discount6: "",
            discount7: "",
            discount8: "",
            discount9: "",
            discount10: ""
        });

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);

    // Initialize states for formdata

    const param = useParams();
    let subscription_type = param.type;
    if (subscription_type !== 'day' && subscription_type !== 'month') {
        subscription_type = 'month'
    }

    const [formData, setFormData] = useState<SubscriptionDataItem>({
        location: [],
        subscription: subscription_type,
        washname: '',
        washdetails: '',
        washnumber: '1',
        maxwashperday: '',
        amount: '',
        preAuthAmount: '',
        isTrial: false,
        trialPeriod: '0',
        trialAmount: '',
        isFree: false,
        washTime:'',
        priceForExtraTime:'',
        hasVacuumWash: false,
        numberOfVacuumWashes: '0',
        maxVacuumTime: '0',
        userFamilyDiscount:null, 
        canExtentSingleWash: true,
        isPriceHoldEnabled: false,
        isPriceHoldEmail: false
        })
    // Initialise states for errors
    const [errors, setErrors] = useState({
        location: '',
        subscription: '',
        washname: '',
        washdetails: '',
        washnumber: '',
        maxwashperday: '',
        amount: '',
        preAuthAmount: '',
        trialAmount: '',
        trialPeriod: '',
        washTime:"",
        priceForExtraTime:"",
         hasVacuumWash: '',
        numberOfVacuumWashes: '',
        maxVacuumTime: ''
    });

    // State to handle selected location
    const [selectedLocation, setSelectedLocation] = useState([]);

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const [loading, setLoading] = useState(true);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const toaster = new MessageToaster();
    const location = useLocation()
    const currentBayType = location?.state?.bayType||1

    useEffect(() => {
        getCompany()
        getCompanyAddress();
    }, [])


    useEffect(() => {
        // To display heading
        handleUpdate()
    }, [company])

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: carwash_owner ? subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Add Single Use" :"Add Single Wash") : 'Add Subscription' : company.companyName, url: carwash_owner ? `/${pageURLs.manage_subscriptions}?type=${subscription_type}&bayType=${currentBayType}` : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}`, showBackButton: true, showMainMenu: true, activeLink: carwash_owner ? "manage-subscriptions" : "manage-car-wash" });
    };

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);

            // To get locations under company
            await getCompanyLocations(data.data.carWashGUID)
            setCompanyDetails(data.data)

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };


    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = param.id ? parseInt(param.id) : 0;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
            if (data?.error === 0) {
                setCompany(data.data);
            }
            setLoading(false)
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getCompanyLocations = async (carwashGuid: string) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid)
                : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }


    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });

    };
  
         const setFreeVacuumData = async (e: any) => {
        if (e.target.name === 'hasVacuumWash') {
            if (e.target.checked === true) {
                if(formData.subscription === 'day')
                {
                    setFormData({
                    ...formData,
                    'numberOfVacuumWashes':'1',
                    [e.target.name]: e.target.checked,
                });
                }
                else{
                    setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                });
                }
                
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['hasVacuumWash']: false,
                    ['numberOfVacuumWashes']: '0',
                    ['maxVacuumTime']: '0',
                });
            }
        } 
    }

        const handleDiscountChange = (e:any) => {
    const { name, value } = e.target;
      // Prevent non-numeric input (allow empty and decimals)
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
      return;
    }
    setDiscountData((prev) => ({
      ...prev,
      [name]: value
    }));

       if (!value.trim()) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "This field is required",
      }));
    } else if (isNaN(Number(value))) {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "Please enter a valid number",
      }));
    } else {
      setFamilyPlanErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

    const handleMultiSelectChange = (selectedOption: [], name: string) => {
        setSelectedLocation(selectedOption)
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: selectedOption,
        });

    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'amount' || name === 'preAuthAmount') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/;
            
            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: value,
                });
            }
        } else if (name === 'trialAmount') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^(0|[1-9][0-9]{0,2})?$/
            if (regex.test(value)) {
                const freeChecked = (value == "0") ? true : false
                setFormData({
                    ...formData,
                    [name]: value,
                    ['isFree']: freeChecked,
                });
            }
        } 
        else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    }

    const validateFamilyPlan = (): boolean => {
    const newErrors: any = {};

    Object.entries(discountData).forEach(([key, value]) => {
      if (!value) {
        newErrors[key as keyof DiscountData] = "Family plan amount is required";
      }else if (isNaN(Number(value)))  {
        newErrors[key as keyof DiscountData] = "Please enter a valid number";
      }
   
    });

    setFamilyPlanErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault()
        try {

            formData.userFamilyDiscount = discountData
           
            // Validate form data
            const isFamilyplanEmpty = Object.values(discountData).every(
                (value) => value === "" || value === null
                );
                if(isFamilyPlan &&   !validateFamilyPlan()) 
                {
                    return
                }

            // Validate form data
            if(currentBayType==serviceType.SelfServe)
            {
            await addSelfServeOnlySubscriptionSchema.validate(formData, { abortEarly: false });

            }
            else{
            await addSelfServeSubscriptionSchema.validate(formData, { abortEarly: false });
            }
            const locationIdArray = formData.location && formData.location.map(loc => loc.value)
            console.log(formData.amount)
            const newformData = (!carwash_owner) ? {
                carWashId: param.id ? parseInt(param.id) : 0,
                locationIds: locationIdArray,
                subscriptionType: formData.subscription,
                washName: formData.washname,
                subscriptionDetails: formData.washdetails,
                numberOfWashes: null,
                dailyWashLimit: parseInt(formData.maxwashperday),
                price: parseFloat(formData.amount),
                isTrial: (formData.isTrial) ? 1 : 0,
                trialPeriod: (formData.isTrial && formData.trialPeriod) ? parseInt(formData.trialPeriod) : 0,
                trialAmount: (formData.trialAmount && formData.isTrial && formData.isFree === false) ? parseFloat(formData.trialAmount) : 0,
                // TEMP: Pre-Auth Amount - may need in future
                // ...(formData.subscription === "day" ? { preAuthAmount: formData.preAuthAmount ? parseFloat(formData.preAuthAmount) : 0 } : {}),
                washTime: formData.washTime ? parseInt(formData.washTime):0,
                bayType:currentBayType,
                priceForExtraTime:formData.priceForExtraTime ? parseFloat(formData.priceForExtraTime):0,
                hasVacuumWash: formData.hasVacuumWash ?? 0,
                numberOfVacuumWashes: Number(formData.numberOfVacuumWashes ?? 0),
                maxVacuumTime: Number(formData.maxVacuumTime ?? 0),
                userFamilyDiscount:isFamilyplanEmpty ? null : discountData,
                canExtentSingleWash: formData.canExtentSingleWash,
                isPriceHoldEnabled: formData.isPriceHoldEnabled ? true : false,
                isPriceHoldEmail: formData.isPriceHoldEnabled && formData.isPriceHoldEmail ? true : false
            } : {
                locationIds: locationIdArray,
                subscriptionType: formData.subscription,
                washName: formData.washname,
                subscriptionDetails: formData.washdetails,
                numberOfWashes: null,
                dailyWashLimit: parseInt(formData.maxwashperday),
                price: parseFloat(formData.amount),
                isTrial: (formData.isTrial) ? 1 : 0,
                trialPeriod: (formData.isTrial && formData.trialPeriod) ? parseInt(formData.trialPeriod) : 0,
                trialAmount: (formData.trialAmount && formData.isTrial && formData.isFree === false) ? parseFloat(formData.trialAmount) : 0,
                // TEMP: Pre-Auth Amount - may need in future
                // ...(formData.subscription === "day" ? { preAuthAmount: formData.preAuthAmount ? parseFloat(formData.preAuthAmount) : 0 } : {}),
                washTime: formData.washTime ? parseInt(formData.washTime) : 0,
                bayType:currentBayType,
                priceForExtraTime:formData.priceForExtraTime ? parseFloat(formData.priceForExtraTime):0,
                hasVacuumWash: formData.hasVacuumWash ,
                numberOfVacuumWashes: Number(formData.numberOfVacuumWashes ?? 0),
                maxVacuumTime: Number(formData.maxVacuumTime ?? 0),
                userFamilyDiscount:isFamilyplanEmpty ? null : discountData,
                canExtentSingleWash : formData.canExtentSingleWash,
                isPriceHoldEnabled: formData.isPriceHoldEnabled ? true : false,
                isPriceHoldEmail: formData.isPriceHoldEnabled && formData.isPriceHoldEmail ? true : false

            }

            try {
                setLoading(true)
                const { data } = (!carwash_owner) ?
                    await axios.post(SubscriptionApi.ADD_SUBSCRIPTION, newformData)
                    : await axios.post(SubscriptionApi.CARWASH_OWNER_ADD_SUBSCRIPTION, newformData);
                setLoading(false)

                if (data?.error === 0) {
                    //Clear errors
                    setErrors({
                        location: '',
                        subscription: '',
                        washname: '',
                        washdetails: '',
                        washnumber: '',
                        maxwashperday: '',
                        amount: '',
                        preAuthAmount: '',
                        trialAmount: '',
                        trialPeriod: '',
                        washTime:'',
                        priceForExtraTime:"",
                         hasVacuumWash: '',
                        numberOfVacuumWashes: '',
                        maxVacuumTime: ''
                    });
                    const subscriptionId = data.data.subscriptionId[0]
                      //Navigate to manage subscription page
                        if (!carwash_owner) {
                            navigate(`/superadmin/${pageURLs.manage_subscriptions}/${param.id}?bayType=${currentBayType}&type=${subscription_type}`);
                        } else {
                            navigate(`/${pageURLs.manage_subscriptions}?bayType=${currentBayType}&type=${subscription_type}`);
                        }
                   
                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }


        } catch (error) {
            const newErrors: any = {};
            console.log(error)
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
            
            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }
        }

    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }



    const setTrailPeriodData = async (e: any) => {
        if (e.target.name === 'isTrial') {
            if (e.target.checked === true) {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                });
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialPeriod']: '0',
                    ['isFree']: false,
                    ['trialAmount']: '',
                });
            }
        } else if (e.target.name === 'isFree') {
            setErrors({
                ...errors,
                ["trialAmount"]: ""
            })
            if (e.target.checked === true) {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            } else {
                setFormData({
                    ...formData,
                    [e.target.name]: e.target.checked,
                    ['trialAmount']: '0',
                });
            }
        }
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                <Breadcrumb>
                    {carwash_owner ? null :
                        <>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_carwash}`) }}>Manage Car Washes</Breadcrumb.Item>
                            <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.carwash_locations}/${param.id}`) }}  >{company.companyName}</Breadcrumb.Item>
                        </>
                    }

                    <Breadcrumb.Item
                        onClick={() => {
                            const url = carwash_owner
                                ? `/${pageURLs.manage_subscriptions}?type=${subscription_type}&bayType=${currentBayType}`
                                : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}`;
                            itemClick(url);
                        }}
                    >
                        {subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Manage Single Use" :"Manage Single Wash") : 'Manage Subscriptions'}
                    </Breadcrumb.Item>

                    <Breadcrumb.Item active>{subscription_type === 'day' ? (currentBayType==serviceType.Vacuum ? "Add Single Use" :"Add Single Wash") : 'Add Subscription'}</Breadcrumb.Item>
                </Breadcrumb>
                {carwash_owner ? null :
                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                        {companyDetails && <GenerateLink id={param.id} companyDetails={companyDetails} carwash_owner={carwash_owner} />}
                        <div className="activity-status d-flex">


                            <CarWashStatus carWashId={param.id} status={(companyDetails?.IsCompanyActive == 1) ? true : false} companyName={company.companyName} />
                            {/* <div className="stat-group d-flex align-items-center">

                                <p>Subscription Status</p>
                                <Badge className='active'>Active</Badge>
                            </div> */}

                        </div>
                    </div>}

                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">

                                <div>
                                    <CWMultiSelect
                                        id="location"
                                        name="location"
                                        options={locations}
                                        label="Select Location"
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        isMandatory
                                        placeholder="Select Location"
                                        errorMessage={errors.location}
                                        onChange={(selectedOptions) => handleMultiSelectChange(selectedOptions, "location")}
                                        hasSelectAll={true}
                                        value={selectedLocation}
                                        labelAllSelected="All Locations Selected."
                                    />
                                </div>
                                {subscription_type != "day" && <CWSelect
                                    id="subscription"
                                    name="subscription"
                                    options={subscriptionTypes}
                                    label="Select Subscription Type"
                                    isMandatory
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select Subscription Type"
                                    multi={false}
                                    searchable={false}
                                    errorMessage={errors.subscription}
                                    values={subscriptionTypes.filter(option => option.value === formData.subscription)}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'subscription')}

                                />}
                                <Row>
                                    <Col md={12}>
                                        <CWInput
                                            id="washname"
                                            name="washname"
                                            type="text"
                                            label={currentBayType==serviceType.Vacuum ? "Vacuum Name": "Wash Name"}
                                            maxLength={WASH_NAME_MAX_LENGTH}
                                            isMandatory
                                            value={formData.washname}
                                            errorMessage={(errors?.washname && errors?.washname!="" ) && currentBayType==serviceType.Vacuum ? "Enter Vacuum Name":errors.washname}
                                            onChange={handleInputChange}
                                        />
                                    </Col>

                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="custom-note position-relative">
                                            <CWInput
                                                id="washTime"
                                                name="washTime"
                                                type="number"
                                                label="Select Time (Minutes)"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.washTime}
                                                errorMessage={errors.washTime  && currentBayType==serviceType.Vacuum ? "Enter valid vacuum time.":errors.washTime}
                                                onChange={handleInputChange}
                                                // readOnly={formData.subscription === "day"}
                                            />
                                            {/* <span className="note"><i>*Single Wash value will be set to 1 by default</i></span> */}
                                        </div>

                                    </Col>
                                    <Col md={6}>
                                        <CWInputGroup
                                            id="amount"
                                            name="amount"
                                            type="number"
                                            step='0.01'
                                            label={formData.subscription === "day" ?  "$ per X Minute(Where X is the Selected Time)" : "Amount" }
                                            labelPrefix="$"
                                            isMandatory
                                            maxLength={AMOUNT_MAX_LENGTH}
                                            value={formData.amount}
                                            errorMessage={errors.amount}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    </Row>
                                    {formData.subscription === "month" ? (
                                        <Row>
                                            <Col md={6}>
                                                <CWCheckbox
                                                    id="isPriceHoldEnabled"
                                                    name="isPriceHoldEnabled"
                                                    label="60-Day Price Hold After Update"
                                                    checked={formData.isPriceHoldEnabled}
                                                    onChange={(e) => {
                                                        setFormData({
                                                            ...formData,
                                                            [e.target.name]: e.target.checked,
                                                            isPriceHoldEmail: e.target.checked ? formData.isPriceHoldEmail : false,
                                                        });
                                                    }}
                                                />
                                            </Col>
                                            {formData.isPriceHoldEnabled ? (
                                                <Col md={12}>
                                                    <CWCheckbox
                                                        id="isPriceHoldEmail"
                                                        name="isPriceHoldEmail"
                                                        label="Notify customers by email about subscription price updates"
                                                        helptext="Checked: Send an email notification to customers. Unchecked: No email notification will be sent."
                                                        checked={formData.isPriceHoldEmail}
                                                        onChange={(e) => {
                                                            setFormData({
                                                                ...formData,
                                                                [e.target.name]: e.target.checked,
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            ) : null}
                                        </Row>
                                    ) : null}
                                    {formData.subscription === "day" && currentBayType==serviceType.Vacuum ?
                                    <Row>
                                        <Col md={6}>
                                            <CWCheckbox
                                        id="hasExtension"
                                        name="hasExtension"
                                        label="Additional Time"
                                        checked={formData.canExtentSingleWash === true || formData.canExtentSingleWash === 1}
                                        onChange={(e) => { 
                                            setFormData({
                                                ...formData,
                                                canExtentSingleWash: e.target.checked
                                            });
                                        }}
                                    />
                                        </Col>
                                    </Row>
                                        :""}
                                    {formData.subscription === "day" ? (
                                    <Row>
                                    {(formData.canExtentSingleWash === true || formData.canExtentSingleWash === 1) ?
                                        <Col md={6}>
                                            <CWInput
                                                id="priceForExtraTime"
                                                name="priceForExtraTime"
                                                type="number"
                                                label="Cost per each additional minute"
                                                maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.priceForExtraTime}
                                                errorMessage={errors.priceForExtraTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col> :  ""
                                    }
                                        {/* TEMP: Pre-Auth Amount - may need in future
                                        <Col md={6}>
                                            <CWInputGroup
                                                id="preAuthAmount"
                                                name="preAuthAmount"
                                                type="number"
                                                step='0.01'
                                                label="Pre-Auth Amount"
                                                labelSuffix="(It is recommended to set the pre-authorization amount to be greater than or equal to the maximum wash amount.)"
                                                labelPrefix="$"
                                                isMandatory
                                                maxLength={AMOUNT_MAX_LENGTH}
                                                value={formData.preAuthAmount}
                                                errorMessage={errors.preAuthAmount}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                        */}
                                    </Row>
                                    ) : null}
                                <Row>
                                    {/* free vacuum wash */}
{  currentBayType!=serviceType.Vacuum ? <div className="trial-sec mt-4">
                                    <CWCheckbox
                                        id="hasVacuumWash"
                                        name="hasVacuumWash"
                                        label="Offer Free Vacuum / Mat Washer"
                                        checked={formData.hasVacuumWash}
                                        onChange={(e) => { setFreeVacuumData(e); }}
                                    />
                                    {formData.hasVacuumWash  ? <Row>
                                        {formData.subscription === 'month' ? <Col md={6}>
                                            <CWInput
                                                id="numberOfVacuumWashes"
                                                name="numberOfVacuumWashes"
                                                label="Number of Times per Month"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.numberOfVacuumWashes}
                                                errorMessage={errors.numberOfVacuumWashes}
                                                onChange={handleInputChange}
                                            />
                                        </Col> :""}
                                        <Col md={6}>
                                                <CWInput
                                                id="maxVacuumTime"
                                                name="maxVacuumTime"
                                                label="Maximum Time per Use (in minutes)"
                                                 maxLength={WASH_NUMBER_MAX_LENGTH}
                                                isMandatory
                                                value={formData.maxVacuumTime}
                                                errorMessage={errors.maxVacuumTime}
                                                onChange={handleInputChange}
                                            />
                                        </Col>
                                    </Row> : ''}
                                </div> : ''}
                                </Row>
                                {formData.subscription === "month" ?<CWCheckbox
                                    id="Isfamilyplan"
                                    name="Isfamilyplan"
                                    label="Family Plan"
                                    checked={isFamilyPlan}
                                    onChange={(e) => { setIsFamilyPlan(e.target.checked); }}
                                />:""}
                                 {
                                 isFamilyPlan ?
                                 <Row>
        {[...Array(10)].map((_, index) => (
          <Col md={4} key={index}>
            <CWInputGroup
              id={`discount${index + 1}`}
              name={`discount${index + 1}`}
              type="number"
              step='0.01'
              label={`Amount to be paid for Family Plan (Sub User ${index + 1}${index === 0 ? ', not account owner' : ''})`}
              labelPrefix="$"
              maxLength={AMOUNT_MAX_LENGTH}
              value={discountData[`discount${index + 1}`] || ""}
              errorMessage={familyPlanErrors[`discount${index + 1}`]}
              onChange={handleDiscountChange}
            />
          </Col>
        ))}
      </Row>   :""} 
                              
                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => {
                                const url = carwash_owner
                                    ? `/${pageURLs.manage_subscriptions}?bayType=${currentBayType}&type=${subscription_type}`
                                    : `/superadmin/${pageURLs.manage_subscriptions}/${param.id}?bayType=${currentBayType}`;
                                navigate(url);
                            }}>Cancel
                            </Button>
                            <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                        </div>
                    </form>
                </div>
            </Container>
            {loading && <SmallLoader />}

        </div >
    )
}

export default AddSelfServeSubscription;
